/**
 * Telemetry & Analytics Service (F-16)
 *
 * Client-side telemetry using localStorage.
 * Captures feature usage, errors, latency, and user actions.
 * All operations are wrapped in try/catch to never break the main app flow.
 */

// ── Types ──────────────────────────────────────────────────────────────

export type TelemetryEventType =
    | 'feature_usage'
    | 'error'
    | 'performance'
    | 'user_action';

export interface TelemetryEvent {
    type: TelemetryEventType;
    feature: string;
    action: string;
    duration?: number;          // milliseconds (for performance events)
    metadata?: Record<string, unknown>;
    timestamp: string;          // ISO 8601
    sessionId: string;
}

export interface AnalyticsSummary {
    totalEvents: number;
    featureUsage: Record<string, number>;
    actionBreakdown: Record<string, number>;
    averageLatency: number | null;    // ms, for 'performance' events
    errorRate: number;                // errors / total events
    acceptRate: number | null;        // applies / (applies + discards)
    topErrors: { message: string; count: number }[];
    periodStart: string | null;
    periodEnd: string | null;
}

// ── Constants ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'ai-edit-telemetry';
const MAX_EVENTS = 100;

// ── Session ID ─────────────────────────────────────────────────────────

let _sessionId: string | null = null;

const getSessionId = (): string => {
    if (!_sessionId) {
        _sessionId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    }
    return _sessionId;
};

// ── Core Functions ─────────────────────────────────────────────────────

/**
 * Record a telemetry event. Safe to call anywhere — never throws.
 */
export const trackEvent = (
    type: TelemetryEventType,
    feature: string,
    action: string,
    metadata?: Record<string, unknown>,
    duration?: number,
): void => {
    try {
        const event: TelemetryEvent = {
            type,
            feature,
            action,
            duration,
            metadata,
            timestamp: new Date().toISOString(),
            sessionId: getSessionId(),
        };

        const events = readEvents();
        events.push(event);

        // Ring buffer: keep only the most recent MAX_EVENTS
        const trimmed = events.length > MAX_EVENTS
            ? events.slice(events.length - MAX_EVENTS)
            : events;

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
        } catch (storageErr) {
            if (storageErr instanceof DOMException && storageErr.name === 'QuotaExceededError') {
                // Flush old events and retry with just the new event
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify([event]));
                } catch {
                    // Storage is completely full — silently skip telemetry
                }
            }
        }
    } catch {
        // Never break the main app — silently ignore unexpected errors
    }
};

/**
 * Convenience: track a timed operation. Returns the duration in ms.
 */
export const trackTiming = (
    feature: string,
    action: string,
    startTime: number,
    metadata?: Record<string, unknown>,
): number => {
    const duration = Date.now() - startTime;
    trackEvent('performance', feature, action, metadata, duration);
    return duration;
};

/**
 * Read all stored events.
 */
export const getEvents = (): TelemetryEvent[] => {
    return readEvents();
};

/**
 * Clear all stored events.
 */
export const clearEvents = (): void => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore
    }
};

/**
 * Compute aggregate analytics from stored events.
 */
export const getAnalyticsSummary = (): AnalyticsSummary => {
    const events = readEvents();

    const featureUsage: Record<string, number> = {};
    const actionBreakdown: Record<string, number> = {};
    const errorMessages: Record<string, number> = {};
    let totalLatency = 0;
    let latencyCount = 0;
    let errorCount = 0;
    let applyCount = 0;
    let discardCount = 0;

    for (const e of events) {
        // Feature usage counts
        featureUsage[e.feature] = (featureUsage[e.feature] || 0) + 1;

        // Action breakdown
        const key = `${e.feature}:${e.action}`;
        actionBreakdown[key] = (actionBreakdown[key] || 0) + 1;

        // Latency
        if (e.type === 'performance' && typeof e.duration === 'number') {
            totalLatency += e.duration;
            latencyCount++;
        }

        // Errors
        if (e.type === 'error') {
            errorCount++;
            const msg = e.metadata?.message || e.action || 'unknown';
            errorMessages[msg] = (errorMessages[msg] || 0) + 1;
        }

        // Accept / discard
        if (e.action === 'apply') applyCount++;
        if (e.action === 'discard') discardCount++;
    }

    const topErrors = Object.entries(errorMessages)
        .map(([message, count]) => ({ message, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

    const totalAcceptDiscard = applyCount + discardCount;

    return {
        totalEvents: events.length,
        featureUsage,
        actionBreakdown,
        averageLatency: latencyCount > 0 ? Math.round(totalLatency / latencyCount) : null,
        errorRate: events.length > 0 ? errorCount / events.length : 0,
        acceptRate: totalAcceptDiscard > 0 ? applyCount / totalAcceptDiscard : null,
        topErrors,
        periodStart: events.length > 0 ? events[0].timestamp : null,
        periodEnd: events.length > 0 ? events[events.length - 1].timestamp : null,
    };
};

// ── Internal Helpers ───────────────────────────────────────────────────

function readEvents(): TelemetryEvent[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}
